import type { ResolvedPromoContent, DisplayMode } from "../types";

/**
 * Map gradient direction to CSS value
 */
export function getGradientDirection(direction?: string | null): string {
    const directionMap: Record<string, string> = {
        "to-right": "to right",
        "to-bottom-right": "to bottom right",
        "to-bottom": "to bottom",
        "to-bottom-left": "to bottom left",
        "to-left": "to left",
    };
    return directionMap[direction ?? "to-bottom-right"] ?? "to bottom right";
}

/**
 * Map overlay position to CSS background-position
 */
export function getOverlayPosition(position?: string | null): string {
    const positionMap: Record<string, string> = {
        "top-right": "top right",
        "top-left": "top left",
        center: "center",
        "bottom-right": "bottom right",
        "bottom-left": "bottom left",
        cover: "center",
    };
    return positionMap[position ?? "top-right"] ?? "top right";
}

/**
 * Build CSS background value from promo branding options
 */
export function buildBackground(promo: ResolvedPromoContent, displayMode: DisplayMode): string {
    if (promo.backgroundOverride) {
        return promo.backgroundOverride;
    }

    const gradientFrom = promo.gradientFrom ?? (displayMode === "card" ? "#222222" : "#FF6B35");
    const gradientTo = promo.gradientTo ?? (displayMode === "card" ? "#444444" : "#E63946");
    const direction = getGradientDirection(promo.gradientDirection);

    const gradient = `linear-gradient(${direction}, ${gradientFrom}, ${gradientTo})`;

    if (promo.overlayImageUrl) {
        const position = displayMode === "card" ? "center" : getOverlayPosition(promo.overlayPosition);
        const sizing = displayMode === "card" || promo.overlayPosition === "cover" ? "cover" : "auto 100%";
        return `url('${promo.overlayImageUrl}') ${position} / ${sizing} no-repeat, ${gradient}`;
    }

    return gradient;
}

/**
 * Determine link properties from ctaLink field.
 * Internal links start with "/", external links start with "http".
 */
export function getLinkProps(ctaLink: string | null | undefined, basePath: string) {
    if (!ctaLink) return { href: null, isExternal: false };
    const isExternal = ctaLink.startsWith("http");
    const href = isExternal ? ctaLink : `${basePath}${ctaLink}`;
    return { href, isExternal };
}
